import { History, Location } from 'history';
import { AuthenticationStorage } from './authenticationStorage';
import { ExternalReference } from '../types';
import { EXTERNAL_REFERENCE_PARAMS } from '../constants';

/**
 * External Reference Authentication Helpers
 *
 * Pure functions for handling nameId & source parameter authentication.
 * Separated for better testability and maintainability.
 */

/**
 * Extract external reference parameters from URL search string
 * @param search - URL search string
 * @returns External reference object or null if nameId not present
 */
export function extractExternalReferenceFromUrl(
  search: string
): ExternalReference | null {
  const params = new URLSearchParams(search);
  const nameId = params.get(EXTERNAL_REFERENCE_PARAMS.NAME_ID);
  const source = params.get(EXTERNAL_REFERENCE_PARAMS.SOURCE);

  // Either nameId OR source can trigger external reference authentication
  if (nameId || source) {
    return {
      nameId: nameId || '',
      source: source || ''
    };
  }

  return null;
}

/**
 * Extract external reference parameters from stored redirect URL
 * @returns External reference object or null if nameId not present
 */
export function extractExternalReferenceFromStorage(): ExternalReference | null {
  const storedRedirectUrl = AuthenticationStorage.getRedirectUrl();

  if (!storedRedirectUrl) {
    return null;
  }

  try {
    const url = new URL(storedRedirectUrl, window.location.origin);
    return extractExternalReferenceFromUrl(url.search);
  } catch (_e) {
    // Invalid stored URL
    return null;
  }
}

/**
 * Extract external reference parameters with priority handling
 * Priority: Current URL params, fallback to stored redirect URL params
 * @param currentSearch - Current URL search string
 * @returns External reference object or null if nameId not present
 */
export function extractExternalReference(
  currentSearch: string
): ExternalReference | null {
  // Priority: Current URL params first
  const currentReference = extractExternalReferenceFromUrl(currentSearch);
  if (currentReference) {
    return currentReference;
  }

  // Fallback: Stored redirect URL params
  return extractExternalReferenceFromStorage();
}

/**
 * Clear external reference parameters from current URL
 * @param location - React Router location object
 * @param history - React Router history object
 */
export function clearExternalReferenceFromCurrentUrl(
  location: Location,
  history: History
): void {
  clearParametersFromCurrentUrl(
    [EXTERNAL_REFERENCE_PARAMS.NAME_ID, EXTERNAL_REFERENCE_PARAMS.SOURCE],
    location,
    history
  );
}

/**
 * Clear external reference parameters from stored redirect URL
 */
export function clearExternalReferenceFromStorage(): void {
  clearParametersFromStorage([
    EXTERNAL_REFERENCE_PARAMS.NAME_ID,
    EXTERNAL_REFERENCE_PARAMS.SOURCE
  ]);
}

/**
 * Check if URL contains external reference parameters
 * @param search - URL search string
 * @returns True if nameId OR source is present
 */
/**
 * Clear specific parameter(s) from current URL
 */
function clearParametersFromCurrentUrl(
  parameterNames: string[],
  location: Location,
  history: History
): void {
  const searchParams = new URLSearchParams(location.search);
  let hasAnyParams = false;

  for (const parameterName of parameterNames) {
    if (searchParams.has(parameterName)) {
      searchParams.delete(parameterName);
      hasAnyParams = true;
    }
  }

  if (hasAnyParams) {
    const newSearch = searchParams.toString();
    const newUrl = newSearch ? `?${newSearch}` : location.pathname;
    history.replace(newUrl);
  }
}

/**
 * Clear specific parameter(s) from stored redirect URL
 */
function clearParametersFromStorage(parameterNames: string[]): void {
  const storedRedirectUrl = AuthenticationStorage.getRedirectUrl();
  if (storedRedirectUrl) {
    try {
      const url = new URL(storedRedirectUrl, window.location.origin);
      let hasAnyParams = false;

      for (const parameterName of parameterNames) {
        if (url.searchParams.has(parameterName)) {
          url.searchParams.delete(parameterName);
          hasAnyParams = true;
        }
      }

      if (hasAnyParams) {
        const cleanedUrl = url.pathname + (url.search || '');
        AuthenticationStorage.setRedirectUrl(cleanedUrl);
      }
    } catch (_e) {
      AuthenticationStorage.removeRedirectUrl();
    }
  }
}

/**
 * Clear only nameId parameter from current URL
 */
export function clearNameIdFromCurrentUrl(
  location: Location,
  history: History
): void {
  clearParametersFromCurrentUrl(
    [EXTERNAL_REFERENCE_PARAMS.NAME_ID],
    location,
    history
  );
}

/**
 * Clear only source parameter from current URL
 */
export function clearSourceFromCurrentUrl(
  location: Location,
  history: History
): void {
  clearParametersFromCurrentUrl(
    [EXTERNAL_REFERENCE_PARAMS.SOURCE],
    location,
    history
  );
}

/**
 * Clear only nameId parameter from stored redirect URL
 */
export function clearNameIdFromStorage(): void {
  clearParametersFromStorage([EXTERNAL_REFERENCE_PARAMS.NAME_ID]);
}

/**
 * Clear only source parameter from stored redirect URL
 */
export function clearSourceFromStorage(): void {
  clearParametersFromStorage([EXTERNAL_REFERENCE_PARAMS.SOURCE]);
}

export function hasExternalReferenceInUrl(search: string): boolean {
  const params = new URLSearchParams(search);
  return (
    params.has(EXTERNAL_REFERENCE_PARAMS.NAME_ID) ||
    params.has(EXTERNAL_REFERENCE_PARAMS.SOURCE)
  );
}
